<template>
  <div class="data-table">
    <div class="data-table-container" v-if="hasResults() || isLoading">
      <table v-if="hasResults() && !isLoading" class="table is-fullwidth">
        <thead>
          <tr>
            <th v-for="column in columns" :key="column.name">
              <div class="column-heading">
                <div v-if="column.sortable" class="data-table-column sortable" @click="sortColumn(column.name)">
                  <DataTableIcon
                    v-if="column.icon"
                    :icon="column.icon"
                    :class="{ 'icon--gap-fix': requiresAlignmentFix(column.icon) }"
                  ></DataTableIcon>
                  {{ column.display }}
                  <div :class="getSortClass(column.name)"></div>
                </div>
                <div v-if="!column.sortable" class="data-table-column">
                  <DataTableIcon
                    v-if="column.icon"
                    :icon="column.icon"
                    :class="{ 'icon--gap-fix': requiresAlignmentFix(column.icon) }"
                  ></DataTableIcon>
                  {{ column.display }}
                </div>
              </div>
            </th>
            <th v-if="!!slots['actions']"></th>
          </tr>
        </thead>
        <tbody>
          <tr class="data-item" v-for="row in data" :key="row.id">
            <td
              class="data-cell"
              :class="{ 'cell-wrap': column.wrappable }"
              v-for="column in columns"
              :key="column.name"
            >
              <div v-if="!slots[column.name]" class="default-cell-display">
                <a class="clickable-cell" v-if="isCellClickable(row, column.clickable)" @click="cellClicked(row)">
                  {{ getDisplayValue(row, column) }}
                </a>
                <span v-if="!isCellClickable(row, column.clickable)">
                  {{ getDisplayValue(row, column) }}
                </span>
              </div>

              <slot v-if="!!slots[column.name]" :name="column.name" v-bind:row="row"></slot>
            </td>
            <td class="actions-column" v-if="!!slots['actions']">
              <slot name="actions" v-bind:row="row"></slot>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="loading-spinner-container" v-if="isLoading">
        <div class="loading-spinner">
          <n-loading-spinner />
        </div>
      </div>
    </div>
    <PagingControl
      class="pagination-controls"
      v-if="pagination && hasResults()"
      @page-changed="onPageChanged"
      :total="total"
      :rows-per-page="rowsPerPage"
    ></PagingControl>

    <slot v-if="!hasResults() && !isLoading" name="nodata" />
  </div>
</template>

<script lang="ts" setup>
import PagingControl, { Page } from '@/shared/components/PagingControl.vue';
import DataTableColumn from '@/shared/components/dataTable/dataTableColumn';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import PageData from '@/shared/components/dataTable/pageData';
import DataTableIcon from '@/shared/components/dataTable/DataTableIcon.vue';
import { PropType, ref, watch, onMounted, useSlots } from 'vue';

const props = defineProps({
  columns: {
    type: Array as PropType<DataTableColumn[]>,
    required: true,
  },
  data: {
    type: Array as PropType<any[]>,
    required: true,
  },
  total: {
    type: Number,
    default: 0,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  pagination: {
    type: Boolean,
    default: false,
  },
  rowsPerPage: {
    type: Number,
  },
  defaultSortBy: {
    type: String,
    default: 'asc',
  },
  defaultSortDirection: {
    type: String as PropType<'asc' | 'desc'>,
    default: '',
  },
});

const emit = defineEmits<{
  (e: 'load', page: PageData): void;
  (e: 'cellClicked', row: any): void;
}>();

const slots = useSlots();
const isLoading = ref(false);
const offset = ref(0);
const sortBy = ref<string>('');
const sortDirection = ref<'asc' | 'desc'>('asc');

watch(
  () => props.loading,
  (loading) => (isLoading.value = loading),
  { immediate: true },
);
watch(
  () => props.data,
  () => (isLoading.value = false),
  { immediate: true },
);

onMounted(() => {
  sortBy.value = props.defaultSortBy;
  sortDirection.value = props.defaultSortDirection;
});

function onPageChanged(page: Page) {
  offset.value = page.start;
  loadData();
}

function loadData() {
  isLoading.value = true;
  emit('load', {
    offset: offset.value,
    sortBy: sortBy.value,
    sortDirection: sortDirection.value,
  } as PageData);
}

function getSortClass(column: string) {
  if (column !== sortBy.value) {
    return '';
  } else {
    return sortDirection.value;
  }
}

function sortColumn(column: string) {
  if (column === sortBy.value) {
    if (sortDirection.value === 'desc') {
      sortDirection.value = 'asc';
    } else if (sortDirection.value === 'asc') {
      sortDirection.value = 'desc';
    }
  } else {
    sortDirection.value = 'desc';
    sortBy.value = column;
  }
  loadData();
}

function isCellClickable(row: any, clickableConfig?: boolean | ((row: any) => boolean)): boolean {
  if (typeof clickableConfig === 'function') {
    return clickableConfig(row);
  }
  return !!clickableConfig;
}

function cellClicked(row: any) {
  emit('cellClicked', row);
}

function hasResults() {
  if (props.pagination) {
    return props.total > 0 && props.data && props.data.length > 0;
  } else {
    return props.data && props.data.length > 0;
  }
}

function requiresAlignmentFix(icon: string) {
  return icon === 'DecimalIcon';
}

function getDisplayValue(row: any, column: DataTableColumn): string {
  if (column.formatter) {
    return column.formatter(row);
  }
  return row[column.name];
}
</script>

<style scoped lang="scss">
.table {
  td,
  th {
    white-space: nowrap;

    &.cell-wrap {
      white-space: normal;
    }
  }

  td {
    &.actions-column {
      width: 48px;
      text-align: left;
    }
  }
}

.column-heading {
  display: flex;
}

.data-table-column {
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  height: 20px;
  gap: 5px;
}

.icon--gap-fix {
  margin-right: -2px;
}

.sortable {
  user-select: none;
  color: black;
  cursor: pointer;
  &:hover {
    color: #0073ee;

    .desc {
      border-top: 6px solid #0073ee;
    }

    .asc {
      border-bottom: 6px solid #0073ee;
    }
  }

  .desc {
    margin-left: 5px;
    margin-top: 6px;
    border: 5px solid transparent;
    border-top: 6px solid black;
  }

  .asc {
    margin-left: 5px;
    display: flex;
    border: 5px solid transparent;
    border-bottom: 6px solid black;
    margin-bottom: 5px;
  }
}

:deep(.sk-folding-cube) {
  margin: 6rem auto;
}

.data-table {
  position: relative;
  width: 100%;
}

.data-table-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;

  .loading-spinner-container {
    display: flex;
    justify-content: center;
    min-height: 250px;

    .loading-spinner {
      width: 50px;
      height: 50px;
    }
  }
}

:deep(.dropdown) {
  position: absolute;

  .dropdown-trigger {
    position: relative;
    top: -2px;
  }

  .dropdown-menu {
    top: calc(100% - 5px);
  }
}
</style>
